import React, {useContext, useState, useRef, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {Button, Position, Tooltip} from '@blueprintjs/core';

import {breakpoints, isSize} from "/src/styles/variables";
import {errorToast, infoToast, successToast} from '/src/utils/toaster';
import {statElems} from '/src/services/api';
import {device} from '/src/services/iot-api';

import {Card as BpCard} from '@blueprintjs/core';
import {Card} from '/src/components/card';

import {ElemsCanvas, ElemsContext, Area, StatElems} from '@auravisionlabs/aura-react';
import {CreateStandardElems} from './create-standard-elems';
import {MenuEditShapes} from './menu';
import {MoveRecordingDialog} from './move-recording';


const PageWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5em;
`

// ----------------------------------------------------- canvas css
// --------- i.e. canvas, standard shapes, changes unsaved and undo
const initialCanvasContainerSize = [
    breakpoints.PHONE_SIZE, breakpoints.TABLET_SIZE
].includes(isSize()) ? "100%" : "50%";

const CanvasContainer = styled.div`
    display: inline-block;
    width: ${initialCanvasContainerSize};
    min-width: 360px;
    max-width: 1080px;
    border: solid 1px black;
    resize: horizontal;
    overflow: hidden;
`;

const BelowCanvasRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 1em;
`

const SaveUndoBlock = styled.div`
    display: flex;
    gap: 1em;
    align-items: center;
`


// ----------------------------------------------------- menu css
const MenuContainer = styled.div`
    display: inline-block;
    min-width: 360px;
    max-width: 1080px;
    flex-grow: 1;
`


function EditShapes({
    currentActiveMenuSection,
    loadingTaxonomies,
    keysAndValuesInUse,
    taxonomiesInUse,
}) {
    const ctx = useContext(ElemsContext);
    const hasThumbnail = !!ctx.recording?.thumbnails?.[0]?.url;

    const [history, setHistory] = useState([]);
    const [compact, setCompact] = useState(false);
    const canvasContainerRef = useRef(null);

    useEffect(() => {
        const el = canvasContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect?.width ?? 0;
            setCompact(width < 600);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    // Refs so keyboard handler always sees current values
    const historyRef = useRef(history);
    const ctxRef = useRef(ctx);
    historyRef.current = history;
    ctxRef.current = ctx;

    const handleSaveChanges = useCallback(() => {
        const {recording, elems} = ctxRef.current;
        return statElems.create(
            recording.id,
            elems.toApi()
        ).then(response => {
            successToast({message: 'All changes were successfully done!'});
            setHistory([]);
            const deviceName = recording.cameraServer?.name;
            if (deviceName) {
                infoToast({message: `Updating handled recids on ${deviceName}...`});
                device.command('UpdateHandledRecids', deviceName, null, 'update').catch(() => {});
            }
            return Promise.resolve(response);
        }).catch(error => {
            errorToast({message: 'Failed to save changes'});
            return Promise.reject(error);
        })
    }, []);

    const beforeChange = useCallback(() => {
        const {elems} = ctxRef.current;
        if (!elems.changing) {
            elems.changing = true;
            setHistory(prev => [...prev, JSON.stringify(elems.toApi())]);
        }
    }, []);

    const afterChange = useCallback(() => {
        const {elems, setElems} = ctxRef.current;
        elems.changing = false;
        // New reference triggers React re-render → canvas auto-redraws
        setElems(new StatElems(elems.scale, elems.elems));
    }, []);

    const undo = useCallback(() => {
        const h = historyRef.current;
        if (h.length) {
            const newHistory = [...h];
            const last = newHistory.pop();
            const {elems, setElems} = ctxRef.current;
            setElems(StatElems.fromApi(elems.scale, JSON.parse(last)));
            setHistory(newHistory);
        }
    }, []);

    // Keyboard handler
    useEffect(() => {
        const keyboard = (event) => {
            const {elems, selected, setSelected} = ctxRef.current;
            if (event.ctrlKey === true) {
                switch (event.key) {
                    case 'z':
                        event.stopPropagation();
                        event.preventDefault();
                        undo();
                        break;
                    case 'l':
                        event.stopPropagation();
                        event.preventDefault();
                        if (elems) {
                            let names = elems.curves.map(a => a.name);
                            let name_idx = 0;
                            while (names.includes(`line-${name_idx}`)) {
                                name_idx += 1
                            }
                            beforeChange();
                            elems.addDefault('lines', `line-${name_idx}`)
                            afterChange();
                        }
                        break;
                    case 'a':
                        event.stopPropagation();
                        event.preventDefault();
                        if (elems) {
                            let names = elems.areas.map(a => a.name);
                            let name_idx = 0;
                            while (names.includes(`area-${name_idx}`)) {
                                name_idx += 1
                            }
                            beforeChange();
                            elems.addDefault('areas', `area-${name_idx}`)
                            afterChange();
                        }
                        break;
                    case 's':
                        event.stopPropagation();
                        event.preventDefault();
                        handleSaveChanges().then();
                        break;
                    default:
                    // do nothing
                }
            } else if (event.key === 'Escape') {
                const {canvasMode, setCanvasMode} = ctxRef.current;
                if (canvasMode) {
                    event.stopPropagation();
                    event.preventDefault();
                    setCanvasMode(null);
                }
            } else if (event.key === 'p' && selected && Object.getPrototypeOf(selected) === Area.prototype) {
                event.stopPropagation();
                event.preventDefault();
                ctxRef.current.setCanvasMode({ type: 'add-point', area: selected });
            } else if (event.key === 'd' && selected && Object.getPrototypeOf(selected) === Area.prototype && selected.poly.length > 3) {
                event.stopPropagation();
                event.preventDefault();
                ctxRef.current.setCanvasMode({ type: 'remove-point', area: selected });
            } else if (event.key === 'Delete' && selected) {
                event.stopPropagation();
                event.preventDefault();
                beforeChange();
                elems.remove(selected);
                setSelected(null);
                afterChange();
            }
        };

        document.addEventListener('keydown', keyboard);
        return () => document.removeEventListener('keydown', keyboard);
    }, [undo, beforeChange, afterChange, handleSaveChanges]);

    const handleElemsAction = useCallback((action) => {
        if (action.action === "save-changes") {
            handleSaveChanges().then(() => {
                console.log("Saved changes");
            });
        }
    }, [handleSaveChanges]);

    return (
        <PageWrapper>
            <CanvasContainer ref={canvasContainerRef}>
                    <ElemsCanvas
                        editable={hasThumbnail}
                        onBeforeChange={beforeChange}
                        onAfterChange={afterChange}
                    />
                    {hasThumbnail && (
                        <BelowCanvasRow>
                            <CreateStandardElems
                                compact={compact}
                                handleSaveChanges={handleSaveChanges}
                            />
                            {!!history.length && (
                                <SaveUndoBlock>
                                    <Button
                                        intent="warning"
                                        onClick={handleSaveChanges}
                                        text={compact ? undefined : "Save Changes"}
                                        icon="floppy-disk"
                                    />
                                    <Tooltip position={Position.TOP_RIGHT} content={'CTRL+Z'}>
                                        <Button
                                            onClick={undo}
                                            icon="undo"
                                            text={compact ? undefined : "Undo"}
                                        />
                                    </Tooltip>
                                </SaveUndoBlock>
                            )}
                        </BelowCanvasRow>
                    )}
                </CanvasContainer>
            <MenuContainer>
                {hasThumbnail && (
                    <BpCard>
                        <MenuEditShapes
                            currentActiveMenuSection={currentActiveMenuSection}
                            loadingTaxonomies={loadingTaxonomies}
                            taxonomiesInUse={taxonomiesInUse}
                            keysAndValuesInUse={keysAndValuesInUse}
                            beforeChange={beforeChange}
                            afterChange={afterChange}
                            onElemsAction={handleElemsAction}
                        />
                    </BpCard>
                )}
                {ctx.recording && (
                    <Card title="Welcome to the Danger Zone" intent="danger">
                        <MoveRecordingDialog recording={ctx.recording} />
                    </Card>
                )}
            </MenuContainer>
        </PageWrapper>
    );
}

export {EditShapes};
